import { useEffect, useState } from "react";
import {
  BUILT_VERSION,
  fetchServerVersion,
  getLastServerVersion,
  isVersionMismatch,
  isInRefreshCooldown,
  refreshCooldownRemainingMs,
  subscribeServerVersion,
} from "../lib/version";

/** Polls /api/version on a slow interval AND subscribes to whatever
 *  server-version values the api.ts helper has already observed
 *  through its X-App-Version header reads. The combination means a
 *  user who's actively clicking around picks up a deploy as soon as
 *  the next API call returns; an idle tab parked on a chrome-only
 *  view picks it up at most one poll interval later (60 s default).
 *
 *  Returns the live server version string (if any), the kind tag
 *  pulled from the polled response (used by the UpdateBanner to gate
 *  major-only popups), and a `mismatch` flag.
 *
 *  Layer C of the banner-noise reduction (polish-26): the banner
 *  only renders when serverKind === 'major'. Minor + patch
 *  mismatches set `mismatch=true` so the WhatsNew sparkle-dot count
 *  picks them up, but no popup interrupts the user. */
export interface VersionWatcherState {
  serverVersion: string | null;
  serverKind: "major" | "minor" | "patch" | null;
  builtVersion: string;
  mismatch: boolean;
}

const POLL_INTERVAL_MS = 60_000;

export function useVersionWatcher(): VersionWatcherState {
  const [serverVersion, setServerVersion] = useState<string | null>(getLastServerVersion);
  const [serverKind, setServerKind] = useState<"major" | "minor" | "patch" | null>(null);
  // `cooldownActive` flips off when the post-refresh cooldown
  // expires. We re-check on a 1 s tick while the cooldown is active
  // so the banner can become visible the moment the window passes
  // (without waiting for the next 60 s server poll).
  const [cooldownActive, setCooldownActive] = useState<boolean>(isInRefreshCooldown);

  useEffect(() => {
    let cancelled = false;

    const probe = async () => {
      const v = await fetchServerVersion();
      if (cancelled) return;
      if (v?.version) setServerVersion(v.version);
      if (v?.kind !== undefined) setServerKind(v.kind ?? null);
    };

    // First probe runs immediately so the server version is known
    // within the first paint cycle, not after a 60 s delay.
    probe();

    // Re-probe on visibility change as well: the polling timer is
    // throttled in backgrounded tabs, so a phone returning from
    // sleep would otherwise sit on a stale value for up to a minute.
    const onVisible = () => {
      if (document.visibilityState === "visible") probe();
    };
    document.addEventListener("visibilitychange", onVisible);

    const t = window.setInterval(probe, POLL_INTERVAL_MS);

    const unsub = subscribeServerVersion((v) => {
      if (!cancelled) setServerVersion(v);
    });

    return () => {
      cancelled = true;
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      unsub();
    };
  }, []);

  // While the post-refresh cooldown is active, tick every second so
  // the banner re-evaluates and unhides the moment the cooldown
  // expires (rather than waiting for a re-render via a state change
  // somewhere else).
  useEffect(() => {
    if (!cooldownActive) return;
    const interval = window.setInterval(() => {
      if (refreshCooldownRemainingMs() <= 0) {
        setCooldownActive(false);
        window.clearInterval(interval);
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [cooldownActive]);

  const rawMismatch = isVersionMismatch(serverVersion);
  // Suppress the banner during cooldown - even if the comparison
  // still says mismatch (which would only happen in a transient
  // SW-activation window now that backend reads dist/version.json
  // as the source of truth, but we keep the safety net).
  const mismatch = rawMismatch && !cooldownActive;

  return {
    serverVersion,
    serverKind,
    builtVersion: BUILT_VERSION,
    mismatch,
  };
}
